import { ApolloClient, createHttpLink, DefaultOptions, from, InMemoryCache, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { getAccessToken } from './util/auth.util';
import { getCurrentCompanyId } from './util/token.util';

const httpLink = createHttpLink({
  uri: `${process.env.REACT_APP_API_BASE_URL}/graphql`,
});

const authLink = setContext((_, { headers }) => {
  headers ||= {};

  // Return headers to the context so httpLink can read them
  return {
    headers: {
      'X-Frame-Options': 'sameorigin',
      'X-Content-Type-Options': 'nosniff',
      'Permissions-Policy': '*',
      authorization: `Bearer ${getAccessToken()}`,
      'X-Company': headers['X-Company'] || getCurrentCompanyId(),
    },
  };
});

let activeSocket: any, timedOut: NodeJS.Timeout;
const wsLink = new GraphQLWsLink(
  createClient({
    url: `${process.env.REACT_APP_WS_BASE_URL}/graphql`,
    keepAlive: 30000, // 30s
    on: {
      connected: socket => {
        activeSocket = socket;
      },
      ping: received => {
        // Give backend 5s to answer
        if (!received) {
          timedOut = setTimeout(() => {
            if (activeSocket.readyState === WebSocket.OPEN) {
              activeSocket.close(4408, 'Request Timeout');
              console.info('Ping Request Timeout');
            }
          }, 5000);
        }
      },
      pong: received => {
        if (received) clearTimeout(timedOut);
      },
    },
    connectionParams: () => {
      return {
        Authorization: `Bearer ${getAccessToken()}`,
      };
    },
  }),
);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  httpLink,
);

const defaultOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'none',
  },
  query: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'none',
  },
};

const cache = new InMemoryCache({
  resultCaching: true,
  addTypename: false,
});

const apolloClient = () =>
  new ApolloClient({
    link: from([authLink, splitLink]),
    defaultOptions,
    cache,
  });

export default apolloClient;
